// The two columns: one row per pair, only the rows in view drawn (books have 1,000 to 2,000).
// Each cell shows its pieces with a divider at every paragraph break (decision 11). A cell
// takes a caret when clicked or on Return, but typing into it is refused; Cmd+E opens an
// editor in its place (decision 10).

import { useVirtualizer } from '@tanstack/react-virtual'
import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { Rows } from '../core/rows.ts'
import { anyFlag, cellOf, flagWords, type Column, type RowView } from '../core/types.ts'

export interface Cursor {
  row: number
  column: Column
}

export interface Editing extends Cursor {
  text: string
}

/// A run of cells in one column, rows `first` to `last`.
export interface Cells {
  column: Column
  first: number
  last: number
}

const holds = (cells: Cells | null, row: number, column: Column) => !!cells && cells.column === column && row >= cells.first && row <= cells.last

interface GridProps {
  rows: Rows
  version: number
  len: number
  notesStart: number | null
  cursor: Cursor
  /// The selected run, when it is more than the cursor's cell.
  selection: Cells | null
  caret: boolean
  editing: Editing | null
  pin: Cells | null
  languages: [string, string]
  /// Bumped to draw every cell afresh, which undoes anything typed into one.
  nonce: number
  scrollTo: { row: number; seq: number }
  /// A click: `caret` to put a caret where it landed, `extend` (Shift) to select from the cursor.
  onPick: (cursor: Cursor, caret: boolean, extend: boolean) => void
  onEditText: (text: string) => void
  onEditDone: (confirm: boolean) => void
  onTyped: () => void
}

export function Grid(p: GridProps) {
  const scroller = useRef<HTMLDivElement>(null)
  const virtualizer = useVirtualizer({
    count: p.len,
    getScrollElement: () => scroller.current,
    estimateSize: () => 52,
    overscan: 8,
  })
  const items = virtualizer.getVirtualItems()
  const first = items[0]?.index ?? 0
  const last = items.at(-1)?.index ?? 0

  useEffect(() => {
    p.rows.ensure(first - 40, last + 40)
  }, [p.rows, first, last, p.version])

  useEffect(() => {
    if (p.scrollTo.seq > 0) virtualizer.scrollToIndex(p.scrollTo.row, { align: 'auto' })
  }, [p.scrollTo, virtualizer])

  return (
    <div className="grid-scroll" ref={scroller} tabIndex={0} aria-label="Rows" data-grid>
      <div className="grid-rows" style={{ height: virtualizer.getTotalSize() }}>
        {items.map(item => {
          const view = p.rows.get(item.index)
          return (
            <div key={item.key} className="grid-item" data-index={item.index} ref={virtualizer.measureElement} style={{ transform: `translateY(${item.start}px)` }}>
              {view ? <RowItem index={item.index} view={view} {...p} /> : <div className="row loading" data-row={item.index} />}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function RowItem(props: GridProps & { index: number; view: RowView }) {
  const { index, view, cursor, notesStart } = props
  const active = cursor.row === index
  const classes = ['row', active && 'active', view.locked && 'locked', view.excluded && 'excluded', view.flags.crosses_paragraph && 'crosses', view.flags.reviewed && 'reviewed'].filter(Boolean).join(' ')
  const flags = flagWords(view)
  return (
    <div className={classes} data-row={index} data-locked={view.locked} data-excluded={view.excluded}>
      {index === notesStart && <div className="notes-band">Notes</div>}
      <div className="cells">
        <div className="num" onMouseDown={e => (e.preventDefault(), props.onPick({ row: index, column: cursor.column }, false, e.shiftKey))}>
          <span>{index + 1}</span>
          {view.locked && <i className="lock" title="Locked" />}
        </div>
        <Cell {...props} column="Source" />
        <div className="lane" />
        <Cell {...props} column="Target" />
        <div className="marks" title={flags.join(', ')}>
          {anyFlag(view.flags) && <i className="flag" data-flags={flags.join(',')} />}
          {view.flags.crosses_paragraph && <span className="pilcrow" title="Crosses a paragraph break">¶</span>}
        </div>
      </div>
      {active && <Meta view={view} index={index} flags={flags} />}
    </div>
  )
}

function Cell(props: GridProps & { index: number; view: RowView; column: Column }) {
  const { index, view, column, cursor, editing, pin } = props
  const cell = cellOf(view, column)
  const blank = cell.pieces.length === 0
  const current = cursor.row === index && cursor.column === column
  const picked = holds(pin, index, column)
  const selected = holds(props.selection, index, column)
  const isEditing = editing?.row === index && editing.column === column
  const lang = props.languages[column === 'Source' ? 0 : 1]
  const classes = ['cell', column, current && 'current', current && props.caret && 'caret', selected && 'selected', blank && 'blank', cell.edited && 'edited', picked && 'picked', isEditing && 'editing'].filter(Boolean).join(' ')
  const refuse = (e: React.SyntheticEvent) => e.preventDefault()
  if (isEditing) {
    return (
      <div className={classes} data-row={index} data-column={column} lang={lang}>
        <EditBox text={editing.text} onText={props.onEditText} onDone={props.onEditDone} />
      </div>
    )
  }
  return (
    <div
      key={props.nonce}
      className={classes}
      data-row={index}
      data-column={column}
      lang={lang}
      contentEditable={blank ? undefined : true}
      // A click or Return gives a cell the focus; Tab never does, so the grid is one stop in the
      // page's tab order and Tab can leave it after Escape.
      tabIndex={-1}
      suppressContentEditableWarning
      spellCheck={false}
      inputMode="none"
      onBeforeInput={refuse}
      onPaste={refuse}
      onCut={refuse}
      onDrop={refuse}
      onInput={props.onTyped}
      onMouseDown={e => {
        // Shift with a click selects from the cursor to here, with no caret and no text selected.
        if (e.shiftKey) e.preventDefault()
        props.onPick({ row: index, column }, !blank && !e.shiftKey, e.shiftKey)
      }}
    >
      {cell.pieces.map((piece, k) => (
        <Fragment key={k}>
          {k > 0 && <div className="pbreak" contentEditable={false} aria-label="paragraph break" />}
          <div data-piece={k} className={['piece', piece.heading && 'heading', piece.note && 'note'].filter(Boolean).join(' ')}>
            {piece.text}
          </div>
        </Fragment>
      ))}
    </div>
  )
}

/// The row's details under it while it is the cursor's: its flags in words, its score, its
/// marks, and the document's text under an edited cell.
function Meta({ view, index, flags }: { view: RowView; index: number; flags: string[] }) {
  const original = (c: Column) => cellOf(view, c).original
  return (
    <div className="rowmeta" data-meta={index}>
      <span className="num">Row {index + 1}</span>
      {view.score !== null && <span className="word" title="What the aligner would charge for this row; lower is more certain">score {view.score.toFixed(1)}</span>}
      {view.locked && <span className="word">locked</span>}
      {view.excluded && <span className="word">excluded from the export</span>}
      {flags.length > 0 && <span className="fl">{flags.join(', ')}</span>}
      {(['Source', 'Target'] as const).map(c =>
        original(c) !== null ? (
          <span key={c} className="was">
            {c === 'Source' ? 'Source' : 'Target'} was: {original(c)!.replaceAll('\n', ' ¶ ')}
          </span>
        ) : null,
      )}
    </div>
  )
}

/// The editor in place of a cell (Cmd+E): Return confirms, Escape cancels, Shift+Return starts
/// a new paragraph.
function EditBox({ text, onText, onDone }: { text: string; onText: (t: string) => void; onDone: (confirm: boolean) => void }) {
  const area = useRef<HTMLTextAreaElement>(null)
  const [height, setHeight] = useState<number | undefined>(undefined)
  useLayoutEffect(() => {
    const a = area.current
    if (!a) return
    a.style.height = 'auto'
    setHeight(a.scrollHeight + 2)
  }, [text])
  useEffect(() => {
    const a = area.current
    if (a) {
      a.focus({ preventScroll: true })
      a.setSelectionRange(a.value.length, a.value.length)
    }
  }, [])
  return (
    <textarea
      ref={area}
      className="editbox"
      value={text}
      spellCheck={false}
      style={height ? { height } : undefined}
      onChange={e => onText(e.target.value)}
      onKeyDown={e => {
        e.stopPropagation()
        if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
          e.preventDefault()
          onDone(true)
        } else if (e.key === 'Escape') {
          e.preventDefault()
          onDone(false)
        }
      }}
      onBlur={() => onDone(true)}
    />
  )
}
